// Notification Settings Page - User controls for all notification types
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, BellOff, Moon, Sun, MessageSquare, Calendar, Clock } from 'lucide-react';
import { notificationScheduler, NotificationSettings } from '@/services/notificationScheduler';
import { PushNotificationService } from '@/services/pushNotifications';

export default function NotificationSettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<NotificationSettings>(notificationScheduler.getSettings());
  const [testingCalendar, setTestingCalendar] = useState(false);
  const [testingDeadline, setTestingDeadline] = useState(false);
  const [testingMessage, setTestingMessage] = useState(false);

  // Save settings whenever they change
  useEffect(() => {
    notificationScheduler.updateSettings(settings);
  }, [settings]);

  const handleTestCalendar = async () => {
    setTestingCalendar(true);
    try {
      await PushNotificationService.testPushNotification();
      alert('✅ Calendar notification test sent! Check your notification center.');
    } catch (error) {
      alert('❌ Failed to send test notification. Make sure notifications are enabled.');
    }
    setTestingCalendar(false);
  };

  const handleTestDeadline = async () => {
    setTestingDeadline(true);
    try {
      await PushNotificationService.showPersistentNotification(
        '⏰ Due Tomorrow',
        'Test assignment due tomorrow at 11:59 PM',
        { tag: 'test-deadline' }
      );
      alert('✅ Deadline notification test sent!');
    } catch (error) {
      alert('❌ Failed to send test notification.');
    }
    setTestingDeadline(false);
  };

  const handleTestMessage = async () => {
    setTestingMessage(true);
    try {
      await PushNotificationService.showPersistentNotification(
        '💬 Message from Test User',
        'This is a test message notification!',
        { tag: 'test-message' }
      );
      alert('✅ Message notification test sent!');
    } catch (error) {
      alert('❌ Failed to send test notification.');
    }
    setTestingMessage(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#05020b] via-[#0a0513] to-[#0f0820] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/organizer/settings')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Notification Settings</h1>
              <p className="text-sm text-purple-100">Manage reminders and alerts</p>
            </div>
          </div>
          <Bell className="w-6 h-6 text-white" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        
        {/* Calendar Event Notifications */}
        <div className="bg-slate-800/40 backdrop-blur-xl border border-purple-500/30 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-purple-400" />
              <div>
                <h2 className="text-lg font-semibold text-white">Calendar Events</h2>
                <p className="text-sm text-slate-400">Reminders for upcoming meetings & appointments</p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, calendarEnabled: !settings.calendarEnabled })}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                settings.calendarEnabled
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-700 text-slate-300'
              }`}
            >
              {settings.calendarEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {settings.calendarEnabled && (
            <div className="space-y-3 pl-9">
              <p className="text-sm text-slate-300 font-medium">Remind me:</p>
              
              {[
                { value: '15min', label: '15 minutes before' },
                { value: '1hour', label: '1 hour before' },
                { value: '1day', label: '1 day before' }
              ].map(option => (
                <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.calendarReminders.includes(option.value as any)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSettings({
                          ...settings,
                          calendarReminders: [...settings.calendarReminders, option.value as any]
                        });
                      } else {
                        setSettings({
                          ...settings,
                          calendarReminders: settings.calendarReminders.filter(r => r !== option.value)
                        });
                      }
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-slate-200">{option.label}</span>
                </label>
              ))}

              <button
                onClick={handleTestCalendar}
                disabled={testingCalendar}
                className="mt-4 px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/40 rounded-lg text-sm text-purple-200 transition-all disabled:opacity-50"
              >
                {testingCalendar ? 'Sending...' : '🧪 Test Calendar Notification'}
              </button>
            </div>
          )}
        </div>

        {/* Deadline/Assignment Notifications */}
        <div className="bg-slate-800/40 backdrop-blur-xl border border-orange-500/30 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-orange-400" />
              <div>
                <h2 className="text-lg font-semibold text-white">Due Dates & Deadlines</h2>
                <p className="text-sm text-slate-400">Reminders for assignments and tasks</p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, deadlinesEnabled: !settings.deadlinesEnabled })}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                settings.deadlinesEnabled
                  ? 'bg-orange-600 text-white'
                  : 'bg-slate-700 text-slate-300'
              }`}
            >
              {settings.deadlinesEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {settings.deadlinesEnabled && (
            <div className="space-y-3 pl-9">
              <p className="text-sm text-slate-300 font-medium">Remind me:</p>
              
              {[
                { value: '24hours', label: '24 hours before due date' },
                { value: 'morning', label: 'Morning of due date (8 AM)' },
                { value: 'overdue', label: 'When items become overdue' }
              ].map(option => (
                <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.deadlineReminders.includes(option.value as any)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSettings({
                          ...settings,
                          deadlineReminders: [...settings.deadlineReminders, option.value as any]
                        });
                      } else {
                        setSettings({
                          ...settings,
                          deadlineReminders: settings.deadlineReminders.filter(r => r !== option.value)
                        });
                      }
                    }}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm text-slate-200">{option.label}</span>
                </label>
              ))}

              <button
                onClick={handleTestDeadline}
                disabled={testingDeadline}
                className="mt-4 px-4 py-2 bg-orange-600/20 hover:bg-orange-600/30 border border-orange-500/40 rounded-lg text-sm text-orange-200 transition-all disabled:opacity-50"
              >
                {testingDeadline ? 'Sending...' : '🧪 Test Deadline Notification'}
              </button>
            </div>
          )}
        </div>

        {/* Message Notifications */}
        <div className="bg-slate-800/40 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-6 h-6 text-cyan-400" />
              <div>
                <h2 className="text-lg font-semibold text-white">Messages</h2>
                <p className="text-sm text-slate-400">Notifications for new messages</p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, messagesEnabled: !settings.messagesEnabled })}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                settings.messagesEnabled
                  ? 'bg-cyan-600 text-white'
                  : 'bg-slate-700 text-slate-300'
              }`}
            >
              {settings.messagesEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {settings.messagesEnabled && (
            <div className="space-y-3 pl-9">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.messagePreview}
                  onChange={(e) => setSettings({ ...settings, messagePreview: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-cyan-600 focus:ring-cyan-500"
                />
                <span className="text-sm text-slate-200">Show message preview</span>
              </label>

              <button
                onClick={handleTestMessage}
                disabled={testingMessage}
                className="mt-4 px-4 py-2 bg-cyan-600/20 hover:bg-cyan-600/30 border border-cyan-500/40 rounded-lg text-sm text-cyan-200 transition-all disabled:opacity-50"
              >
                {testingMessage ? 'Sending...' : '🧪 Test Message Notification'}
              </button>
            </div>
          )}
        </div>

        {/* Quiet Hours */}
        <div className="bg-slate-800/40 backdrop-blur-xl border border-indigo-500/30 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Moon className="w-6 h-6 text-indigo-400" />
              <div>
                <h2 className="text-lg font-semibold text-white">Quiet Hours</h2>
                <p className="text-sm text-slate-400">Pause notifications during sleep</p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, quietHoursEnabled: !settings.quietHoursEnabled })}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                settings.quietHoursEnabled
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-700 text-slate-300'
              }`}
            >
              {settings.quietHoursEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {settings.quietHoursEnabled && (
            <div className="space-y-3 pl-9">
              <div className="flex items-center gap-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">From</label>
                  <input
                    type="time"
                    value={settings.quietHoursStart}
                    onChange={(e) => setSettings({ ...settings, quietHoursStart: e.target.value })}
                    className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">To</label>
                  <input
                    type="time"
                    value={settings.quietHoursEnd}
                    onChange={(e) => setSettings({ ...settings, quietHoursEnd: e.target.value })}
                    className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400">
                Notifications will be paused from {settings.quietHoursStart} to {settings.quietHoursEnd}
              </p>
            </div>
          )}
        </div>

        {/* Daily Summary */}
        <div className="bg-slate-800/40 backdrop-blur-xl border border-yellow-500/30 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sun className="w-6 h-6 text-yellow-400" />
              <div>
                <h2 className="text-lg font-semibold text-white">Daily Summary</h2>
                <p className="text-sm text-slate-400">Morning recap of today's tasks</p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, dailySummaryEnabled: !settings.dailySummaryEnabled })}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                settings.dailySummaryEnabled
                  ? 'bg-yellow-600 text-white'
                  : 'bg-slate-700 text-slate-300'
              }`}
            >
              {settings.dailySummaryEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          {settings.dailySummaryEnabled && (
            <div className="space-y-3 pl-9">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Summary Time</label>
                <input
                  type="time"
                  value={settings.dailySummaryTime}
                  onChange={(e) => setSettings({ ...settings, dailySummaryTime: e.target.value })}
                  className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
              <p className="text-xs text-slate-400">
                You'll receive a summary of today's tasks at {settings.dailySummaryTime}
              </p>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-600/10 border border-blue-500/30 rounded-xl p-4">
          <div className="flex gap-3">
            <div className="text-blue-400 flex-shrink-0">ℹ️</div>
            <div className="text-sm text-blue-100 space-y-2">
              <p className="font-medium">How it works:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-200/80">
                <li>Notifications work even when your phone is locked</li>
                <li>Make sure you've allowed notifications for WHIPT</li>
                <li>Quiet hours will pause all notifications except emergencies</li>
                <li>You can test each type to see how they'll appear</li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
